import { useMemo, useState } from "react";
import { Button, Flex, Grid, ModalV2, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useGetPositionsForCaseQuery } from "~/graphql/supergraphHooks";
import { toDbId } from "~/utils/graphql-helpers";
import { DOCUMENT_TYPE_MAPPING } from "../../constants";
import { POSITION_CASE_TYPES } from "./constants";
import {
  columnDefinition,
  crsColumnDefinition,
  crsGridOptions,
  defaultColDef,
  type FatcaPositionRow,
  gridOptions,
} from "./gridConfig";
import * as styles from "./styles_vanilla.css";
import { useCrsGridSelection } from "./useCrsGridSelection";
import {
  buildRowData,
  getCurrentCasePositionGlobalId,
  getIsCurrentCaseInvestorOnboarding,
} from "./utils";

interface FatcaAssignmentModalProps {
  profileId: number;
  profileName: string;
  documentName: string;
  documentType: string;
  caseId: number;
  onClose: () => void;
  onAssign: (positionIds: string[]) => void;
  isAssigning?: boolean;
}

const FatcaAssignmentModal = ({
  profileId,
  profileName,
  documentName,
  documentType,
  caseId,
  onClose,
  onAssign,
  isAssigning = false,
}: FatcaAssignmentModalProps) => {
  const isCrs = documentType === DOCUMENT_TYPE_MAPPING.selfCertification.enumValue;
  const [isExpanded, setIsExpanded] = useState(false);
  const { selectedPositionIds, handleSelectionChanged, handleRowDataUpdated, getRowClass } =
    useCrsGridSelection();

  const { data, loading } = useGetPositionsForCaseQuery({
    variables: { profileId, caseTypes: [...POSITION_CASE_TYPES] },
    fetchPolicy: "network-only",
  });

  const allRowData = useMemo(
    () =>
      buildRowData(
        data?.complianceProfile?.positions ?? [],
        data?.complianceProfile?.complianceCases ?? [],
        profileId,
        caseId,
      ),
    [data, profileId, caseId],
  );

  const currentCaseRow = useMemo(() => allRowData.filter((r) => r.isCurrentCase), [allRowData]);

  const hasCurrentCase = currentCaseRow.length > 0;
  const hasAdditionalPositions = allRowData.length > currentCaseRow.length;

  const currentCasePositionId = useMemo(
    () =>
      getCurrentCasePositionGlobalId(data?.complianceProfile?.complianceCases ?? [], caseId),
    [data, caseId],
  );

  const isCurrentCaseIO = useMemo(
    () =>
      getIsCurrentCaseInvestorOnboarding(
        data?.complianceProfile?.complianceCases ?? [],
        caseId,
      ),
    [data, caseId],
  );

  const currentCaseDbId = useMemo(
    () => (currentCasePositionId ? String(toDbId(currentCasePositionId)) : undefined),
    [currentCasePositionId],
  );

  const assignablePositionIds = useMemo(() => {
    // Payloads mirror the backend resolver's contract, which only distinguishes IO from
    // everything else. IO cases: the resolver self-references the current case, whose open
    // position has no case-map row yet (sending it would fail the linked-position check),
    // so exclude it. All other case types: every resolvable position is map-linked, so send
    // the profile positions plus the case-linked one (a no-op dedupe when already present).
    const allPositions = data?.complianceProfile?.positions ?? [];
    const decoded = allPositions.map((p) => ({
      id: p.id,
      dbId: String(toDbId(p.id)),
    }));
    if (isCurrentCaseIO) {
      const withoutCurrent = decoded.filter((p) => p.id !== currentCasePositionId);
      if (withoutCurrent.length > 0) return withoutCurrent.map((p) => p.dbId);
      return decoded.map((p) => p.dbId);
    }
    const ids = decoded.map((p) => p.dbId);
    if (currentCaseDbId && !ids.includes(currentCaseDbId)) ids.push(currentCaseDbId);
    return ids;
  }, [data, currentCasePositionId, isCurrentCaseIO, currentCaseDbId]);

  const isFatca = documentType === DOCUMENT_TYPE_MAPPING.wSeriesTaxForm.enumValue;
  const assignmentLabel = isFatca ? "FATCA" : "CRS";

  const handleAssign = () => {
    if (isCrs) {
      if (showMultiSelect) {
        onAssign(selectedPositionIds);
      } else {
        onAssign(currentCaseDbId ? [currentCaseDbId] : []);
      }
    } else {
      onAssign(assignablePositionIds);
    }
  };

  const descriptionText = isCrs
    ? `This document will become the new ${assignmentLabel} source document for the specified position(s). This action will override any previously assigned ${assignmentLabel} source document for the position(s).`
    : `This document will become the new ${assignmentLabel} source document for this profile. This action will override any previously assigned ${assignmentLabel} source document for all positions associated with this profile.`;

  const showMultiSelect = isCrs && (isExpanded || !hasCurrentCase);
  const displayedRowData = isCrs && !isExpanded && hasCurrentCase ? currentCaseRow : allRowData;
  const activeGridOptions = showMultiSelect ? crsGridOptions : gridOptions;
  const activeColumnDefs = showMultiSelect ? crsColumnDefinition : columnDefinition;

  return (
    <ModalV2
      data-testid="fatca-assignment-modal"
      show={true}
      onHide={onClose}
      title={`Assign as ${assignmentLabel} source document`}
      size="large"
      closeOnClickOutside={false}
    >
      <ModalV2.Body>
        <Text className={styles.description}>{descriptionText}</Text>
        <Flex className={styles.contextContainer} gap={Sizes.XL}>
          <Flex flexDirection="column" gap={Sizes.XS} className={styles.contextColumn}>
            <Text variant="secondary-md">Profile</Text>
            <Text variant="regular-md">{profileName}</Text>
          </Flex>
          <Flex flexDirection="column" gap={Sizes.XS} className={styles.contextColumn}>
            <Text variant="secondary-md">New {assignmentLabel} data source</Text>
            <Text variant="regular-md">{documentName}</Text>
          </Flex>
        </Flex>
        <div className={styles.gridWrapper}>
          <Grid<FatcaPositionRow>
            gridOptions={{
              ...activeGridOptions,
              ...(showMultiSelect
                ? {
                    onSelectionChanged: handleSelectionChanged,
                    onRowDataUpdated: handleRowDataUpdated,
                    getRowClass,
                  }
                : {}),
            }}
            defaultColDef={defaultColDef}
            columnDefs={activeColumnDefs}
            rowData={loading ? [] : displayedRowData}
          />
        </div>
        {isCrs && hasCurrentCase && hasAdditionalPositions && (
          <div className={styles.expandToggle}>
            <Button
              displayAsLink
              variant="link"
              data-testid="crs-expand-toggle"
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded
                ? "Hide more positions available to assign"
                : "View more positions available to assign"}
            </Button>
          </div>
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex gap={Sizes.MD} justifyContent="flex-end">
          <Button onClick={onClose} variant="neutral" disabled={isAssigning}>
            Cancel
          </Button>
          <Button
            data-testid="fatca-assign-button"
            onClick={handleAssign}
            disabled={
              loading || isAssigning || (showMultiSelect && selectedPositionIds.length === 0)
            }
          >
            {isCrs ? "Assign to position" : "Assign to profile"}
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default FatcaAssignmentModal;
